import {
  OrdersAdditionalOrderDataOptions,
  SvcGetOrder,
  SvcGetOrganizationUnitSummary,
  SvcGetReturnableStatusForOrder,
  SvcGetWebshops,
  SvcListBlobsForOrder,
  SvcListOrderLines,
  SvcListOrderShipments,
  TranslationsListEntityTranslation,
} from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useFormatter } from "next-intl";
import { Fragment, useMemo } from "react";

import IconAppointment from "@/icons/icon-appointment";
import IconCart from "@/icons/icon-cart";
import IconChevronLeft from "@/icons/icon-chevron-left";
import IconCountry from "@/icons/icon-country";
import IconSquareCheck from "@/icons/icon-square-check";
import IconStoreLocator from "@/icons/icon-store-locator";
import IconWallet from "@/icons/icon-wallet";
import CartProductTile, { CartProductTileContext } from "~/components/cart/cart-product-tile";
import HighlightedText from "~/components/common/highlighted-text";
import IconClickAndCollect from "~/components/common/icons/icon-click-and-collect";
import myaccount from "~/components/common/myaccount.module.scss";
import OrderInfo from "~/components/common/order-info";
import InputField from "~/components/form/input-field";
import DiscountLinesTotals from "~/components/products/discount-lines-totals";
import { useAuthContext } from "~/contexts/auth";
import { useConfiguration } from "~/contexts/configuration";
import { useEvaService } from "~/hooks/use-eva-service";
import useGiftcard from "~/hooks/use-giftcard";
import useSubscriptions from "~/hooks/use-subscriptions";
import appRoutes from "~/utils/app-routes";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";
import evaUtils from "~/utils/eva-utils";
import intlUtils from "~/utils/intl-utils";
import loyaltyUtils from "~/utils/loyalty-utils";
import orderUtils from "~/utils/order-utils";
import productUtils from "~/utils/product-utils";

import Icon from "../../common/icon";
import LinkWrapper from "../../common/link-wrapper";
import CancelOrder from "./cancel-order";
import styles from "./order-details.module.scss";
import Progressbar from "./progressbar";

type Props = {
  orderId: string;
  returns?: boolean;
  inGuestPage?: boolean;
  returnReasonsTranslations?: TranslationsListEntityTranslation[];
};

export default function OrderDetail(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const { hasEvaAuth, isAuthenticated } = useAuthContext();
  const formatter = useFormatter();
  const priceFormatter = intlUtils.usePriceFormatter();
  const { configuration, organizationUnitId } = useConfiguration();

  const evaEndpointUrl = process.env.NEXT_PUBLIC_EVA_BACKEND_URL;

  const getOrder = useEvaService(SvcGetOrder);
  const getReturnableStatus = useEvaService(SvcGetReturnableStatusForOrder);
  const listBlobsForOrder = useEvaService(SvcListBlobsForOrder);
  const listOrderLines = useEvaService(SvcListOrderLines);
  const listOrderShipments = useEvaService(SvcListOrderShipments);
  const getOrganizationUnitSummary = useEvaService(SvcGetOrganizationUnitSummary);
  const getWebshops = useEvaService(SvcGetWebshops);

  const { data, error, isLoading } = useQuery({
    queryKey: ["getOrder", props.orderId],
    queryFn: async () => {
      try {
        const order = await getOrder({
          OrderID: props.orderId,
          AdditionalOrderDataOptions: {
            ProductProperties: Constants.ORDER_DETAIL_INCLUDED_FIELDS,
            IncludeProductRequirements: true,
            IncludeGiftWrapping: true,
          } as OrdersAdditionalOrderDataOptions,
        });

        if (!order?.Result) {
          return null;
        }

        if (order.HasMoreLines) {
          order.Result.Lines = await evaUtils.getListOrderLines(listOrderLines, props.orderId, {
            ProductProperties: Constants.ORDER_DETAIL_INCLUDED_FIELDS,
          });
        }

        return {
          order: order.Result,
          products: order.Result.Lines?.filter(
            (line) => !!line.ProductID && ((line.IsCancelled && line.FulfillmentStatus === 800) || !line.IsCancelled)
          ),
          amounts: order.Amounts,
          AdditionalOrderData: order.AdditionalOrderData,
        };
      } catch {
        return null;
      }
    },
    enabled: !!evaEndpointUrl,
  });

  const queryBlobs = useQuery({
    queryKey: ["getAttachedFiles", props.orderId],
    staleTime: Infinity,
    queryFn: async () => {
      const blobsData = await listBlobsForOrder({
        OrderID: props.orderId,
      });

      if (!blobsData) {
        return null;
      }

      return blobsData.Result;
    },
    enabled: !!data,
  });

  const shipmentTracking = useQuery({
    queryKey: ["getOrderShipment", props.orderId],
    staleTime: Constants.SHIPPING_STALE_TIME,
    queryFn: async () => {
      const shipmentsData = await listOrderShipments({
        OrderID: props.orderId,
      });

      if (!shipmentsData?.Result?.length) {
        return null;
      }

      return shipmentsData.Result[0];
    },
    enabled: !!data && data.order.TotalItems !== 0,
  });

  // TODO understand with max if feasable to retrive detailed payment info
  // right now useless since we do not have access to PaymentDetails object
  // const paymentDetails = useQuery({
  //   queryKey: ["getPaymentsDetails", props.orderId],
  //   staleTime: Constants.SHIPPING_STALE_TIME,
  //   queryFn: async () => {
  //     let paymentsDetails: EVA.Core.GetPaymentTransactionResponse[] = [];

  //     data?.order.Payments.map(async (payment) => {
  //       const response = await getPaymentTransaction({
  //         PaymentTransactionID: payment.ID,
  //       });

  //       if (response != undefined && !response?.Error) {
  //         paymentsDetails.push(response);
  //       }
  //     });

  //     return paymentsDetails;
  //   },
  //   enabled: !!data && data.order.Payments.length > 0,
  // });

  const querystatus = useQuery({
    queryKey: ["getReturnableStatus", props.orderId],
    staleTime: Infinity,
    queryFn: async () => {
      const status = await getReturnableStatus({
        OrderID: props.orderId,
      });

      if (!status) {
        return null;
      } else {
        return status.Result;
      }
    },
    enabled: !!data,
  });

  const { data: originatingCountry } = useQuery({
    queryKey: ["getOrganizationUnitSummary", data?.order.OriginatingOrganizationUnitID],
    staleTime: Infinity,
    queryFn: async () => {
      return data?.order.OriginatingOrganizationUnitID
        ? await getOrganizationUnitSummary({
            OrganizationUnitID: data.order.OriginatingOrganizationUnitID,
          })
        : null;
    },
    enabled: data?.order.OriginatingOrganizationUnitID !== organizationUnitId,
  });

  const { data: webshops } = useQuery({
    queryKey: ["getWebshops"],
    staleTime: Infinity,
    queryFn: async () => await getWebshops(),
  });

  const cancelledLines = data?.products?.filter((line) => line.IsCancelled);
  const digitalGiftCardsQuery = useGiftcard().prepareGetDigitalGiftcardOptionsQuery(data?.order);

  // check if the loyalty program is active for this specific organizational unit
  const { hasLoyaltyProgram, isUserSubscribedToLoyalty } = useSubscriptions();

  const returnAvailable = useMemo(() => {
    return querystatus.data?.some((line) => line.IsReturnable);
  }, [querystatus.data]);

  const isOrderEligibleForReturn: boolean = useMemo(() => {
    return data?.order.DeliveryDate ? orderUtils.isOrderEligibleForReturn(data?.order.DeliveryDate) : false;
  }, [data?.order.DeliveryDate]);

  const orderReturnMaxDate: Date | undefined = useMemo(() => {
    return data?.order.DeliveryDate ? orderUtils.getOrderReturnMaxDate(data.order.DeliveryDate) : undefined;
  }, [data?.order.DeliveryDate]);

  const orderReturnDaysFromNow: number | undefined = useMemo(() => {
    return data?.order.DeliveryDate ? orderUtils.getOrderReturnDaysFromNow(data.order.DeliveryDate) : undefined;
  }, [data?.order.DeliveryDate]);

  if (isLoading) {
    return <></>;
  }

  if (!data || error) {
    return (
      <p className={myaccount.infoMessage}>
        {t("generic.noOrderFound", {
          orderId: props.orderId,
        })}
      </p>
    );
  }

  const currency = data.order.CurrencyID;

  const showExplicitTax = configuration.PreferredPriceDisplayMode == 1 && configuration?.CurrencyID == currency;

  const discountLines = data.amounts?.Discounts?.PerDiscount;

  // const firstShippingMethod = data.products.find((line) => line.ShippingMethod && line.ShippingMethod.Name);

  const orderLoyaltyDeposits = data.order?.TotalLoyaltyProgramDeposits;

  return (
    <>
      <Head>
        <title>{t("generic.orderDetailNumber", { number: props.orderId })}</title>
      </Head>
      <div className={styles.orderDetailsWrapper}>
        {props.inGuestPage ? (
          <div className={styles.centeredTitle}>
            <h2>
              <HighlightedText text={t("generic.yourorderdetails")} />
            </h2>
            <p>
              {t("generic.myaccount.orderdetails.order_title_date")}{" "}
              {formatter.dateTime(new Date(data.order.PlacementDate || data.order.CreationTime), { dateStyle: "full" })}
            </p>
          </div>
        ) : (
          <LinkWrapper href={props.returns ? "returns" : "purchases"} className={styles.orderDetailsTitle}>
            <h2>
              <Icon icon={IconChevronLeft} aria-hidden className={styles.orderDetailsTitleIcon} />
              {props.returns ? (
                t("generic.myaccount.returndetail.title", { 0: props.orderId })
              ) : (
                <>
                  {t("generic.myaccount.orderdetails.order_title_date")}{" "}
                  {formatter.dateTime(new Date(data.order.PlacementDate || data.order.CreationTime), {
                    dateStyle: "full",
                  })}
                </>
              )}
            </h2>
          </LinkWrapper>
        )}
        <div className={myaccount.myaccountCard}>
          <div className={myaccount.myaccountCardHeader}>
            <h3>{t("generic.myaccount.orderdetails.order_details_title")}</h3>
          </div>
          <div className={myaccount.myaccountCardContent}>
            <ul className={styles.orderDetailsList}>
              <li>
                <Icon icon={IconCart} aria-hidden />
                <div>
                  <p className={styles.orderDetailsItemTitle}>
                    {(data.order.TotalItems || 0) + (cancelledLines || []).length}{" "}
                    {t("generic.myaccount.orderdetails.items_count")} {props.returns ? t("generic.returned") : null}
                  </p>
                  <p>{priceFormatter.currency(Math.abs(data.order.TotalAmountInTax) ?? 0, { currency })}</p>
                </div>
              </li>
              {props.returns ? (
                <li>
                  <Icon icon={IconAppointment} aria-hidden />
                  <div>
                    <p className={styles.orderDetailsItemTitle}>{t("generic.requestdate")}</p>
                    <p>
                      {formatter.dateTime(data.order.PlacementDate ? new Date(data.order.PlacementDate) : new Date(), {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </p>
                  </div>
                </li>
              ) : (
                <li>
                  <Icon
                    icon={
                      !Constants.RETURNABLE_ORDER_TYPES.includes(data.order.OrderCustomType?.BackendID || "") ||
                      data.order.UserAgentApplication === "eva-pos-app"
                        ? IconStoreLocator
                        : data.order.OrderCustomType?.BackendID === Constants.ORDER_TYPE_RESERVE
                        ? IconClickAndCollect
                        : IconCountry
                    }
                    aria-hidden
                  />
                  <div>
                    <p className={styles.orderDetailsItemTitle}>{t("generic.myaccount.orderdetails.purchase_type")}</p>
                    <p>{t(productUtils.getOrderType(data.order))}</p>
                  </div>
                </li>
              )}
              <li>
                <Icon icon={IconSquareCheck} aria-hidden />
                <div>
                  <p className={styles.orderDetailsItemTitle}>
                    {t(props.returns ? "generic.requestnumber" : "generic.myaccount.orderdetails.purchase_number")}
                  </p>
                  <p>{data.order.ID}</p>
                </div>
              </li>
            </ul>
            <p className={styles.orderDetailsStatusTitle}>
              {t("generic.myaccount.orderdetails.status")}: <span>{t(productUtils.getOrderStatus(data.order))}</span>
            </p>
            {/* replace "eva-pos-app" with types filtering as per https://kikocosmetics.atlassian.net/wiki/spaces/UC/pages/93519899/Custom+Order+Types */}
            {!props.returns &&
              (Constants.RETURNABLE_ORDER_TYPES.includes(data.order.OrderCustomType?.BackendID || "") ||
                data.order.UserAgentApplication != "eva-pos-app") && (
                <>
                  {data.order.TotalItems != 0 && <Progressbar status={productUtils.getOrderStatusCode(data.order)} />}
                  {/* show order cancellation if available */}
                  <CancelOrder order={data.order} />
                  {/* FIXME use the order status to show/hide different buttons */}
                  {data.order.TotalItems != 0 && (
                    <div className={styles.orderDetailsStatusButtons}>
                      {data.order.HasDelivery && shipmentTracking.data?.TrackingLink && (
                        <LinkWrapper
                          lookAsButton="primary"
                          href={
                            shipmentTracking.data?.TrackingLink +
                            (shipmentTracking.data?.TrackingCode
                              ? `?trackingId=${shipmentTracking.data?.TrackingCode}`
                              : "")
                          }
                          target="_blank"
                        >
                          {t("generic.track")}
                        </LinkWrapper>
                      )}
                      {data.order.IsInvoiced && queryBlobs.data?.some((blob) => blob.Type == 1) && (
                        <LinkWrapper
                          lookAsButton="secondary"
                          href={`${configuration["Urls:Assets"]}/blob/${
                            queryBlobs.data?.find((blob) => blob.Type == 1)?.BlobID
                          }`}
                          target="_blank"
                          download
                        >
                          {t("generic.download_invoice")}
                        </LinkWrapper>
                      )}
                      {returnAvailable && data.order.IsShipped && orderReturnMaxDate && isOrderEligibleForReturn && (
                        <>
                          {`${data.order.OriginatingOrganizationUnitID}` === `${organizationUnitId}` ? (
                            <>
                              <p className={myaccount.infoMessage}>
                                {t("generic.returns.requestuntil", {
                                  returnWindowDays: orderReturnDaysFromNow!,
                                  maxReturnDate: formatter.dateTime(orderReturnMaxDate, {
                                    dateStyle: "medium",
                                  }),
                                })}
                              </p>
                              <LinkWrapper
                                href={
                                  process.env.NEXT_PUBLIC_RETURNS_ENABLED === "true" &&
                                  configuration["App:Ecommerce:EnableReturns"] === "true"
                                    ? hasEvaAuth && !isAuthenticated
                                      ? appRoutes.REQUEST_RETURN + `/${props.orderId}`
                                      : `purchases/${props.orderId}/request-return`
                                    : appRoutes.CUSTOMER_HELP_CENTER + `/?question=${t("generic.helpcenterquestion")}`
                                }
                                lookAsButton="secondary"
                              >
                                {t("generic.returns.requestcta")}
                              </LinkWrapper>
                            </>
                          ) : originatingCountry !== null ? (
                            <p className={myaccount.infoMessage}>
                              {t("generic.returnFromAnotherCountry", {
                                countryCode: originatingCountry?.Address?.CountryID ?? originatingCountry?.CountryID,
                              })}
                            </p>
                          ) : null}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
          </div>
        </div>

        {data.order.TotalItems != 0 &&
          hasLoyaltyProgram &&
          isUserSubscribedToLoyalty &&
          !props.returns &&
          orderLoyaltyDeposits?.[0]?.Points != undefined && (
            <div className={classNames(myaccount.myaccountCard, styles.orderDetailsKisses)}>
              {isUserSubscribedToLoyalty ? (
                <>
                  {data.amounts?.Paid?.Amount ? (
                    <>
                      <p>
                        {t.rich("generic.withThisOrderYouHaveGained", {
                          points: "" + orderLoyaltyDeposits?.[0]?.Points,
                          br: (chunks) => <br />,
                          strong: (chunks) => <span className={styles.strong}>{chunks}</span>,
                          kissesContainer: (chunks) => <span className={styles.highlighted}>{chunks}</span>,
                        })}
                      </p>
                      {/* TODO: Hidden as per 2023/09/06 agreement. Since we don't have any information about the EVA discount associated to the current tier, and thus to the missing points required to go to the next level */}
                      {/* <p>
                        {t("generic.myaccount.orderdetails.kissesNeeded", { kisses: 50 })}{" "}
                        <span className={styles.highlighted}>50 {t("generic.kisses")}</span>
                        <LinkWrapper href={appRoutes.LOYALTY}>{t("generic.discoverMore")}</LinkWrapper>
                      </p> */}
                    </>
                  ) : (
                    <>
                      <p>
                        {t.rich("generic.withThisOrderYouWillGain", {
                          points: "",
                          br: (chunks) => <br />,
                          strong: (chunks) => <span className={styles.strong}>{chunks}</span>,
                          kissesContainer: (chunks) => <span className={styles.highlighted}>{chunks}</span>,
                          myAccountLink: (chunks) => (
                            <Link className={styles.link} href={appRoutes.MYACCOUNT}>
                              {chunks}
                            </Link>
                          ),
                        })}
                      </p>
                      {/* TODO: Hidden as per 2023/09/06 agreement. Since we don't have any information about the EVA discount associated to the current tier, and thus to the missing points required to go to the next level */}
                      {/* <p>
                        {t("generic.myaccount.orderdetails.kissesNeeded", { kisses: 50 })}{" "}
                        <span className={styles.highlighted}>50 {t("generic.kisses")}</span>
                        <LinkWrapper href={appRoutes.LOYALTY}>{t("generic.discoverMore")}</LinkWrapper>
                      </p> */}
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* TODO: Hidden as per 2023/09/06 agreement. Since we don't have any information about the EVA discount associated to the current tier, and thus to the missing points required to go to the next level */}
                  {/* <p>
                  {t.rich("generic.withThisOrderYouHaveGained", {
                    points: "" + orderLoyaltyDeposits?.[0]?.Points,
                    br: (chunks) => <br />,
                    strong: (chunks) => <span className={styles.strong}>{chunks}</span>,
                    kissesContainer: (chunks) => <span className={styles.highlighted}>{chunks}</span>,
                  })}
                </p>
                {t.rich("generic.myaccount.orderdetails.kissesNeeded", {
                  p: (chunks) => <p>{chunks}</p>,
                  kissesContainer: (chunks) => (
                    <span className={styles.highlighted}>
                      {orderLoyaltyDeposits?.[0]?.Points}
                      {chunks}
                    </span>
                  ),
                  link: (chunks) => <LinkWrapper href={appRoutes.LOYALTY}>{chunks}</LinkWrapper>,
                })} */}
                </>
              )}
            </div>
          )}

        <div className={myaccount.myaccountCard}>
          <div className={myaccount.myaccountCardHeader}>
            <h3 className={styles.orderDetailsItemsListTitle}>
              {t("generic.myaccount.orderdetails.product_list_title", {
                productsLength: (data.order.TotalItems || 0) + (cancelledLines || []).length,
              })}
            </h3>
          </div>
          <div className={myaccount.myaccountCardContent}>
            <div className={styles.orderItems}>
              {data.products?.map((line, idx) => {
                const additionalInfo = evaUtils.getCustomFieldValueForResource(
                  Constants.RETURN_ADDITIONAL_INFO,
                  line,
                  configuration
                );

                const returnReasonTranslation = props.returnReasonsTranslations?.find((i: any) => {
                  return i.EntityID === line.ReturnReason?.ID;
                });

                return (
                  <Fragment key={line.ID}>
                    <CartProductTile
                      line={line}
                      disableCartActions={true}
                      hideOrderQty={props.returns}
                      giftCardData={digitalGiftCardsQuery.data?.find(
                        (card) => `${card.Identifiers?.OrderLineID}` === `${line.ID}`
                      )}
                      physicalGiftCardRequirement={
                        data?.AdditionalOrderData?.ProductRequirements?.find((el) => el.OrderLineID == line.ID)
                          ?.RequirementModels
                      }
                      context={CartProductTileContext.orderHistoryDetail}
                      showNetPriceWithoutPromo={props.returns}
                    />
                    {props.returns && (
                      <div className={styles.additionalReason}>
                        <InputField
                          value={returnReasonTranslation?.Value || line.ReturnReason?.Name}
                          label={t("generic.returns.why")}
                          isOptional
                          readOnly
                          disableButton
                        />
                        {queryBlobs.data &&
                          queryBlobs.data.filter((blob) => blob.OrderLineID && blob.OrderLineID == line.ID).length >
                            0 && (
                            <div className={styles.wrapperImages}>
                              <p>{t("generic.yourPicture")}</p>
                              <div className={styles.imagesList}>
                                {queryBlobs.data
                                  .filter((blob) => blob.OrderLineID && blob.OrderLineID == line.ID)
                                  .map((image) => (
                                    <Image
                                      key={image.BlobID}
                                      width={80}
                                      height={80}
                                      src={`${configuration["Urls:Assets"]}/blob/${image.BlobID}`}
                                      alt={image.Name!}
                                    />
                                  ))}
                              </div>
                            </div>
                          )}
                        {typeof additionalInfo === "string" && additionalInfo.length > 0 && (
                          <InputField
                            value={additionalInfo}
                            label={t("generic.returns.additional")}
                            isOptional
                            readOnly
                            disableButton
                          />
                        )}
                      </div>
                    )}
                    <div className={styles.divider} aria-hidden />
                  </Fragment>
                );
              })}
              <div className={styles.orderResume}>
                <p>
                  <span>{t("generic.subtotal")}</span>
                  {data.amounts?.Types && (
                    <span className={styles.orderResumeValue}>
                      {priceFormatter.currency(
                        Math.abs(
                          showExplicitTax
                            ? data.amounts.Types.NormalProduct.Amount
                            : data.amounts.Types.NormalProduct.InTax
                        ),
                        { currency }
                      )}
                    </span>
                  )}
                </p>
                <DiscountLinesTotals lines={discountLines ?? []} orderId={props.orderId} currency={currency} />
                {data.amounts?.Types?.GiftWrappingCosts &&
                  (data.amounts.Types.GiftWrappingCosts.Amount > 0 ||
                    data.amounts.Types.GiftWrappingCosts.InTax > 0) && (
                    <p>
                      <span>{t("generic.giftWrappingCost")}</span>
                      {data.amounts?.Types && (
                        <span className={styles.orderResumeValue}>
                          {priceFormatter.currency(
                            showExplicitTax
                              ? Math.abs(data.amounts.Types.GiftWrappingCosts.Amount)
                              : Math.abs(data.amounts.Types.GiftWrappingCosts.InTax),
                            { currency }
                          )}
                        </span>
                      )}
                    </p>
                  )}
                {/* TODO remove once the new visualization is approved  */}
                {/* {!props.returns && !!data.amounts.Types?.Discount && (
                  <p>
                    <span>{t("generic.promo")}</span>
                    <span className={styles.orderResumeValue}>
                      {priceFormatter.currency(
                        props.returns ? -data.amounts.Types?.Discount?.InTax : data.amounts.Types?.Discount?.InTax,
                        { currency }
                      )}
                    </span>
                  </p>
                )} */}
                {data.order.HasDelivery && data.order.ShippingAddress?.CountryID && (
                  <p>
                    <span>
                      {t("generic.shipping_to", {
                        destination: crossCountryUtils.getLocalizedCountryName(
                          data.order.ShippingAddress.CountryID,
                          router
                        ),
                        method:
                          data.order.Lines?.find((line) => line.ShippingMethod && !line.IsCancelled)?.ShippingMethod!
                            .Name || "",
                      })}
                    </span>
                    <span className={styles.orderResumeValue}>
                      {priceFormatter.currency(
                        (showExplicitTax
                          ? data.amounts?.Types?.ShippingCosts?.Amount
                          : data.amounts?.Types?.ShippingCosts?.InTax) || 0,
                        { currency }
                      )}
                    </span>
                  </p>
                )}

                {showExplicitTax && data.amounts?.Total && data.amounts.Total.InTax != 0 && (
                  <p>
                    <span>{t("generic.taxes")}</span>
                    <span className={styles.orderResumeValue}>
                      {priceFormatter.currency(Math.abs(data.amounts.Total.Tax), {
                        currency,
                      })}
                    </span>
                  </p>
                )}
                <p className={styles.orderResumeTotal}>
                  <span>{t(props.returns ? "generic.totalrefund" : "generic.total")}</span>
                  <span>{priceFormatter.currency(Math.abs(data.order.TotalAmountInTax), { currency })}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        {!props.returns && (
          <OrderInfo
            order={data.order}
            additionalOrderData={data.AdditionalOrderData}
            className={styles.generalOrderInfo}
            cardClassName={styles.orderInfoCard}
            cardHeaderClassName={styles.orderInfoCardHeader}
            cardContentClassName={myaccount.myaccountCardContent}
            amounts={data.amounts}
          />
        )}

        {props.returns && (
          <div className={myaccount.myaccountCard}>
            <div className={myaccount.myaccountCardHeader}>
              <h3 className={styles.orderDetailsItemsListTitle}>{t("generic.refundinfo")}</h3>
            </div>
            <div className={myaccount.myaccountCardContent}>
              <h4 className={styles.iconTextRow}>
                <Icon icon={IconWallet} aria-hidden className={styles.titleIcon} />
                {t("generic.refundmethod")}
              </h4>

              <p className={styles.bold}>{t("generic.returns.refundOnPaymentMethod")}</p>

              {hasLoyaltyProgram && isUserSubscribedToLoyalty ? (
                <p className={styles.info}>{t("generic.returns.loyaltyPointsRemoved")}</p>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
